// @ts-nocheck
import * as awrtc from "awrtc_browser";
import axios from "axios";

import { TUserAuthData } from "@storage/session/types";
import { appTokenStorage, userAuthDataStorage } from "@storage/sessionStorage";

import { ENV_CONFIG } from "@configs/env";

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
	(response) => response,
	(error) => error,
);

export class Call {
	/**
	 * @type {number}
	 */
	UPDATE_TIMER_TIMEOUT = 50;

	/**
	 * @type {string}
	 */
	MODEL_SERVER_CONFIG_URL = "models/configurations/webrtc";

	/**
	 * @type {string}
	 */
	CLIENT_SERVER_CONFIG_URL = "/api/clients/v1/configurations/call/servers";

	/**
	 * @type {Boolean}
	 */
	isDebug = false;

	/**
	 * @type {awrtc.SmartCall}
	 */
	call;

	/**
	 * @type {Number}
	 */
	updateTimer;

	/**
	 * @type {String}
	 */
	address;

	/**
	 * @type {Element}
	 */
	videoParent;

	/**
	 * @param address {String} Connectivity address. Must be the same for the caller and the called.
	 */
	constructor() {
		this.address = "";
		this.networkConfig = "";
		this.call = null;
	}

	/**
	 * @return {Call}
	 */
	enableDebug() {
		this.isDebug = true;
		return this;
	}

	async configure() {
		try {
			const config = await this.requestConfiguration();
			config.IsConference = true;
			this.networkConfig = config;
			this.call = null;
		} catch (error) {
			this.error(error);
		}
	}
	/**
	 * @param networkConfig {awrtc.NetworkConfig}
	 */

	start(address) {
		if (this.call) {
			this.stop();
		}
		this.address = address;
		//create media configuration
		const mediaConfig = new awrtc.MediaConfig();

		mediaConfig.Audio = false;
		// True by default and must be explicitly turned off.
		// The whole process fails when device is requested but not found.
		mediaConfig.Video = false;

		this.call = new awrtc.SmartCall(this.networkConfig, {
			iceTransportPolicy: "relay",
		});
		//Events handling (get triggered after Configure / Listen call)
		this.call.addEventListener((sender, args) => {
			this.onEvent(sender, args);
		});
		this.call.OnIceSelected = (s) => console.log("webrtc servers", s);
		this.updateTimer = setInterval(() => {
			this.update();
		}, this.UPDATE_TIMER_TIMEOUT);
		this.log("Update timer is initialized.");

		//configure media. This will request access to media and can fail if the user doesn't have a proper device or
		//blocks access

		this.call.Configure(mediaConfig);

		this.log("Configuration is activated.");

		//Trying to listen to the address
		//Conference mode = everyone listening will connect to each other
		//Call mode -> If the address is free it will wait for someone else to connect
		//          -> If the address is used then it will fail to listen and then try to connect via Call(address)
		this.call.Listen(this.address, "model");

		this.log("Listening is initialized.");
		return false;
	}

	stop() {
		if (!this.call) {
			return;
		}
		this.address = "";
		this.call.Dispose();
		this.call = null;
		this.updateTimer = -1;
		this.log("Stopped.");
	}

	update() {
		if (this.call === null) {
			return;
		}
		this.call.Update();
	}

	changeVolume(volume) {
		this.call.Volume = volume;
	}

	mute(muted) {
		this.call.Mute = muted;
	}

	onEvent(sender, args) {
		if (args.Type === awrtc.CallEventType.Invalid) {
			this.error("Event was not properly initialized.");
			this.stop();
			return;
		}

		if (args.Type === awrtc.CallEventType.WaitForIncomingCall) {
			this.log("Awaiting an incoming call.");
			return;
		}

		if (args.Type === awrtc.CallEventType.CallAccepted) {
			this.log("Accepted an incoming call.");
			const userData = userAuthDataStorage.getItemAsJson<TUserAuthData>();
			this.changeVolume(userData?.audio_chat_volume || 0);
			this.mute(userData?.audio_chat_muted || false);
			return;
		}

		if (args.Type === awrtc.CallEventType.CallEnded) {
			this.log("Call has finished.");
			// this.stop();
			return;
		}

		if (args.Type === awrtc.CallEventType.Message || args.Type === awrtc.CallEventType.DataMessage) {
			this.log("Received a data message and ignored it.");
			return;
		}

		if (args.Type === awrtc.CallEventType.ConnectionFailed) {
			this.error("Connection has failed. Offline? Server is dead?");
			this.stop();
			return;
		}

		if (args.Type === awrtc.CallEventType.ListeningFailed) {
			this.error("Listening has failed. Address might be in use or due to server/network error.");
			this.log("Trying to connect to existing call.");
			this.call.Call(this.address);
			return;
		}

		if (args.Type === awrtc.CallEventType.ConfigurationComplete) {
			this.log("Configuration is complete. Microphone access is granted.");
			return;
		}

		if (args.Type === awrtc.CallEventType.ConfigurationFailed) {
			this.error("Configuration has failed. Could not get access to the microphone.");
			return;
		}

		if (args.Type === awrtc.CallEventType.MediaUpdate) {
			const isLocalStream = args.ConnectionId === awrtc.ConnectionId.INVALID;
			if (isLocalStream) {
				// Local video stream is sound from user's microphone.
				this.log("Received local audio stream and ignored it.");
				return;
			}

			return;
		}

		this.error("Unknown event type: " + args.Type);
		this.stop();
	}

	/**
	 * @param isClientCall {Boolean}
	 *
	 * @return {Promise}
	 */
	requestConfiguration() {
		const url = this.MODEL_SERVER_CONFIG_URL;
		const app_token = appTokenStorage.getItem();
		if (app_token) {
			axiosInstance.defaults.headers.common["X-AUTH-TOKEN"] = app_token;
		}
		return axiosInstance
			.get(`${ENV_CONFIG.BASE_API_UTL}${url}`)
			.then(
				(response) => {
					if (response.status === 200 || response.status === 202) {
						if (!("signaling" in response.data)) {
							throw new Error("Could not get signaling server configuration.");
						}
						if (!("url" in response.data.signaling)) {
							throw new Error("Could not get signaling server URL.");
						}

						if (!("turn" in response.data)) {
							throw new Error("Could not get TURN server configuration.");
						}
						if (!("url" in response.data.turn[0])) {
							throw new Error("Could not get TURN server url.");
						}
						if (!("login" in response.data.turn[0])) {
							throw new Error("Could not get TURN server username.");
						}
						if (!("password" in response.data.turn[0])) {
							throw new Error("Could not get TURN server password.");
						}

						const config = new awrtc.NetworkConfig();

						config.IceServers = response.data.turn.map((t) => ({
							urls: [t.url],
							username: t.login,
							credentialType: "password",
							credential: t.password,
						}));
						config.SignalingUrl = response.data.signaling.url;

						return config;
					}
				},
				(reason) => {
					throw new Error(reason);
				},
			)
			.catch((error) => {
				throw error;
			});
	}

	/**
	 * @param message {String}
	 */
	log(message) {
		if (!this.isDebug) {
			return;
		}

		console.log("[Call] " + message);
	}

	/**
	 * @param message {String}
	 */
	error(message) {
		if (!this.isDebug) {
			return;
		}

		console.error("[Call] " + message);
	}
}
